// Root-level provider that owns the New Project intake mutation.
//
// Why this exists: the legacy implementation called `useIntakeSubmit`
// directly inside `NewProjectRoute`. When the operator clicked Submit
// and then navigated away (sidebar click to Operations, Agents, or
// hard-reload during the 30-120s Atlas window), the route unmounted,
// the mutation state was lost, the auto-navigate to `/runs/:id` never
// fired, and the operator had no idea whether their submit was still
// running. The 2026-05-15 Avonmouth incident showed it surfacing as
// "I uploaded, navigated to Agents, came back to Operations, project
// gone".
//
// The fix: lift the mutation here, host it at the AppLayout level (so
// it survives every child-route navigation), surface state via a
// floating pill in the layout, and have AppLayout watch the result
// state to navigate to `/runs/:id` on success regardless of which page
// the operator is on at the moment Atlas finishes. NewProjectRoute
// becomes a thin form-submission caller that fires
// `submit(input)` and forgets - the provider drives everything from
// there. localStorage-backed form-draft persistence in NewProjectRoute
// covers the orthogonal case of a hard reload before submit.
//
// Multi-user note: when Kinde lands and two operators open `/new` at
// the same time, each tab gets its own provider instance (per the
// browser tab boundary) so they're independent. The backend already
// supports concurrent /intake calls for the same project_id (each
// creates a distinct AgentRun). What this provider doesn't solve is
// cross-tab broadcasting; that's a `BroadcastChannel` add when we
// see the need.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "react-query";
import { inferKind, type NewProjectResult } from "../api";
import { useAgentsHttp } from "../api/useAgentsHttp";
import type { TriageRecord } from "../api/models/run";
import {
  IntakeSubmitContext,
  type IntakeSubmitContextValue,
  type IntakeSubmitInput,
  type IntakeSubmitState,
  type SubmitStage,
} from "./useIntakeSubmitContext";

// ---------------------------------------------------------------------- //
// Internal types matching the backend shapes
// ---------------------------------------------------------------------- //

interface UploadInitiateResponse {
  upload_id: string;
  presigned_url: string;
}

interface IntakeResponse {
  triage: TriageRecord;
  run_id: string | null;
}

interface IntakeFileRef {
  upload_id: string;
  kind: string;
}

const guessContentType = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".eml")) return "message/rfc822";
  return "application/octet-stream";
};

// ---------------------------------------------------------------------- //
// Provider
// ---------------------------------------------------------------------- //

const IDLE_STATE: IntakeSubmitState = {
  stage: "idle",
  label: "",
  projectId: null,
  result: null,
  error: null,
  uploadProgress: { current: 0, total: 0 },
};

export const IntakeSubmitProvider = ({ children }: { children: ReactNode }) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const [state, setState] = useState<IntakeSubmitState>(IDLE_STATE);

  // Track in-flight submissions across re-renders. We don't actually
  // wire abort yet (a long Atlas call is best-effort: the backend
  // Lambda keeps running on client disconnect because Function URL
  // is BUFFERED, so aborting the fetch wouldn't stop the work or get
  // any compute saved). But the ref gives us a place to check
  // "should this resolved promise update state, or has a newer
  // submit started since?" - and to wire AbortController later if /
  // when streaming arrives.
  const submitTokenRef = useRef<number>(0);

  // beforeunload guard: warn the operator if they try to close the
  // tab / hard-reload while a submit is in flight. Browser shows a
  // generic confirm dialog; modern browsers ignore custom strings
  // but still show the dialog when `returnValue` is set.
  useEffect(() => {
    const inFlight = state.stage === "uploading" || state.stage === "atlas";
    if (!inFlight) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [state.stage]);

  // Guards against two parallel submits stepping on each other.
  // ``NewProjectRoute``'s canSubmit already disables the button while
  // the provider is busy, but the provider's API doesn't enforce
  // that — and we don't want a future caller (e.g. a hot-key, a
  // programmatic re-submit) to silently overwrite a winning submit's
  // tail-end "done" state. Throw loudly instead.
  const stageRef = useRef<SubmitStage>("idle");
  useEffect(() => {
    stageRef.current = state.stage;
  }, [state.stage]);

  const submit = useCallback(
    async (input: IntakeSubmitInput): Promise<NewProjectResult> => {
      if (stageRef.current === "uploading" || stageRef.current === "atlas") {
        throw new Error(
          "A submit is already in flight. Wait for it to finish (the pill " +
            "in the corner shows progress) before starting another.",
        );
      }
      const myToken = ++submitTokenRef.current;
      const safeSetState = (updater: (s: IntakeSubmitState) => IntakeSubmitState) => {
        // Drop the update if a newer submit has started since - we
        // don't want stale progress events from a cancelled submit
        // overwriting the new submit's state.
        if (submitTokenRef.current !== myToken) return;
        setState(updater);
      };

      const mpans = input.mpans_csv
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      const organizations = input.organizations
        .filter((o) => o.name.trim().length > 0)
        .map((o) => ({
          name: o.name.trim(),
          type: (o.type ?? "").trim() || null,
        }));

      safeSetState(() => ({
        stage: "uploading",
        label: `Preparing to upload ${input.files.length} file${
          input.files.length === 1 ? "" : "s"
        } for ${input.project_id}…`,
        projectId: input.project_id,
        result: null,
        error: null,
        uploadProgress: { current: 0, total: input.files.length },
      }));

      try {
        // STEP 1: upload files sequentially (same as legacy + the
        // earlier `useIntakeSubmit` hook). With small bundles latency
        // is dominated by Atlas anyway.
        const fileRefs: IntakeFileRef[] = [];
        for (let i = 0; i < input.files.length; i++) {
          const f = input.files[i];
          safeSetState((s) => ({
            ...s,
            stage: "uploading",
            label: `Uploading file ${i + 1} of ${input.files.length}: ${f.name}`,
            uploadProgress: { current: i + 1, total: input.files.length },
          }));
          const contentType = f.type || guessContentType(f.name);
          const init = await http<UploadInitiateResponse>(
            "/uploads/initiate",
            {
              project_id: input.project_id,
              original_name: f.name,
              content_type: contentType,
            },
          );
          const putResp = await fetch(init.presigned_url, {
            method: "PUT",
            body: f,
            headers: { "content-type": contentType },
          });
          if (!putResp.ok) {
            const detail = await putResp.text();
            throw new Error(
              `S3 PUT failed for ${f.name}: ${putResp.status} ${detail}`,
            );
          }
          await http("/uploads/complete", {
            upload_id: init.upload_id,
          });
          fileRefs.push({
            upload_id: init.upload_id,
            kind: inferKind(f.name),
          });
        }

        // STEP 2: call /intake. The backend triages, dispatches to
        // Engineer or Grid, and auto-persists an AgentRun.
        safeSetState((s) => ({
          ...s,
          stage: "atlas",
          label: `Atlas working on ${input.project_id} — typically 30-120s`,
        }));

        const findOrg = (t: string) =>
          organizations.find((o) => (o.type ?? "").toLowerCase() === t.toLowerCase())
            ?.name;
        const body = {
          project_id: input.project_id,
          location: input.address
            ? { lat: 51.5, lon: -0.1, address: input.address }
            : { lat: 51.5, lon: -0.1 },
          mpans,
          ask: input.ask || undefined,
          notes:
            [
              input.ask || "",
              organizations.length
                ? "Organisations: " +
                  organizations
                    .map((o) => `${o.name}${o.type ? ` (${o.type})` : ""}`)
                    .join("; ")
                : "",
            ]
              .filter(Boolean)
              .join("\n") || undefined,
          organizations,
          platform_inputs: {
            landlord_name:
              findOrg("Landlord") || findOrg("Owner-occupier") || undefined,
            tenant_name: findOrg("Tenant") || undefined,
          },
          file_refs: fileRefs,
        };

        const wrapped = await http<IntakeResponse>("/intake", body);

        const result: NewProjectResult = {
          project_id: input.project_id,
          triage: wrapped.triage,
          run_id: wrapped.run_id,
        };
        safeSetState(() => ({
          stage: "done",
          label: `Atlas finished ${input.project_id}.`,
          projectId: input.project_id,
          result,
          error: null,
          uploadProgress: { current: input.files.length, total: input.files.length },
        }));
        // Invalidate queries so Operations + runs list pick up the
        // new row when next mounted.
        qc.invalidateQueries({ queryKey: ["inrange-agents-runs-list"] });
        qc.invalidateQueries({ queryKey: ["inrange-agents-projects-list"] });
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        safeSetState(() => ({
          stage: "error",
          label: `Submit failed for ${input.project_id}: ${error.message.slice(0, 200)}`,
          projectId: input.project_id,
          result: null,
          error,
          uploadProgress: { current: 0, total: input.files.length },
        }));
        throw error;
      }
    },
    [http, qc],
  );

  const dismiss = useCallback(() => {
    setState((s) => {
      // Don't dismiss a still-running submit; the pill must stay
      // visible during uploading / atlas stages so the operator
      // remembers what's running.
      if (s.stage === "uploading" || s.stage === "atlas") return s;
      return IDLE_STATE;
    });
  }, []);

  const value = useMemo<IntakeSubmitContextValue>(
    () => ({ state, submit, dismiss }),
    [state, submit, dismiss],
  );

  return (
    <IntakeSubmitContext.Provider value={value}>
      {children}
    </IntakeSubmitContext.Provider>
  );
};
